import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import {
  ModularDataTableProps,
  TablePagination,
  TableSorting,
  TableSelection,
  TableFilter
} from './types';
import { TableHeader } from './components/TableHeader';
import { TableBody } from './components/TableBody';
import { TableFooter } from './components/TableFooter';
import { TableSearch } from './components/TableSearch';
import { TableLoading, TableError } from './components/TableStates';
import { searchInData, sortData, paginateData } from './utils';

export const ModularDataTable: FC<ModularDataTableProps> = ({
  // Dados
  data = [],
  columns,

  // Paginação
  pagination: externalPagination,
  onPaginationChange,

  // Ordenação
  sorting: externalSorting,
  onSortingChange,

  // Filtros e busca
  searchable = true,
  searchPlaceholder = "Pesquisar...",
  onFilterChange,

  // Seleção
  selectable = false,
  selection: externalSelection,
  onSelectionChange,

  // Ações
  actions,

  // Configurações visuais
  striped = true,
  bordered = false,
  hover = true,
  size = 'md',
  className = '',

  // Estados
  loading = false,
  error,

  // Callbacks
  onDataChange,
}) => {
  // Estados internos
  const [internalPagination, setInternalPagination] = useState<TablePagination>({
    page: 1,
    pageSize: 25,
    total: 0,
    pageSizes: [10, 25, 50, 100],
  });

  const [internalSorting, setInternalSorting] = useState<TableSorting>({
    field: '',
    direction: 'asc',
  });

  const [internalSelection, setInternalSelection] = useState<TableSelection>({
    selectedRows: [],
    selectAll: false,
  });

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  // Determina se está usando estado interno ou externo
  const pagination = externalPagination || internalPagination;
  const sorting = externalSorting || internalSorting;
  const selection = externalSelection || internalSelection;

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Processa os dados baseado nos filtros, ordenação e busca
  const processedData = useMemo(() => {
    let result = [...data];

    // Aplica busca
    if (debouncedSearchTerm && !onDataChange) {
      result = searchInData(result, debouncedSearchTerm);
    }

    // Aplica ordenação (apenas se não houver callback externo)
    if (sorting.field && !onDataChange) {
      result = sortData(result, sorting.field, sorting.direction);
    }

    return result;
  }, [data, debouncedSearchTerm, sorting, onDataChange]);

  // Dados paginados (apenas se não houver callback externo)
  const paginatedData = useMemo(() => {
    if (onDataChange) {
      return data; // Se há callback externo, assume que os dados já vêm paginados
    }

    return paginateData(processedData, pagination.page, pagination.pageSize);
  }, [processedData, pagination.page, pagination.pageSize, onDataChange, data]);

  // Atualiza total quando dados mudam
  useEffect(() => {
    if (!externalPagination) {
      setInternalPagination(prev => ({
        ...prev,
        total: processedData.length,
      }));
    }
  }, [processedData.length, externalPagination]);

  // Callback para mudança de paginação
  const handlePaginationChange = useCallback((newPagination: TablePagination) => {
    if (onPaginationChange) {
      onPaginationChange(newPagination);
    } else {
      setInternalPagination(newPagination);
    }

    // Chama callback de mudança de dados se existir
    if (onDataChange) {
      onDataChange({
        page: newPagination.page,
        pageSize: newPagination.pageSize,
        search: debouncedSearchTerm,
        sorting: sorting.field ? sorting : undefined,
      });
    }
  }, [onPaginationChange, onDataChange, debouncedSearchTerm, sorting]);

  // Callback para mudança de ordenação
  const handleSortingChange = useCallback((newSorting: TableSorting) => {
    if (onSortingChange) {
      onSortingChange(newSorting);
    } else {
      setInternalSorting(newSorting);
    }

    // Chama callback de mudança de dados se existir
    if (onDataChange) {
      onDataChange({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: debouncedSearchTerm,
        sorting: newSorting,
      });
    }
  }, [onSortingChange, onDataChange, pagination.page, pagination.pageSize, debouncedSearchTerm]);

  // Callback para mudança de seleção
  const handleSelectionChange = useCallback((newSelection: TableSelection) => {
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    } else {
      setInternalSelection(newSelection);
    }
  }, [onSelectionChange]);

  /*/ Callback para mudança de filtros
  useEffect(() => {    
    if (onDataChange && debouncedSearchTerm !== searchTerm) {
      onDataChange({
        page: 1, // Volta para primeira página ao pesquisar
        pageSize: pagination.pageSize,
        search: searchTerm,
        sorting: sorting.field ? sorting : undefined,
      });
    }
  }, [debouncedSearchTerm, onFilterChange, onDataChange, pagination.pageSize, sorting]);
  */


  useEffect(() => {
    if (onDataChange && debouncedSearchTerm !== searchTerm) {
      onDataChange({
        page: 1, // Volta para primeira página ao pesquisar
        pageSize: pagination.pageSize,
        search: searchTerm,
        sorting: sorting.field ? sorting : undefined,
      });
    }
  }, [debouncedSearchTerm]);

  const tableClasses = [
    'table',
    striped ? 'table-striped' : '',
    bordered ? 'table-bordered' : '',
    hover ? 'table-hover' : '',
    `table-${size}`,
    className,
  ].filter(Boolean).join(' ');

  const visibleColumns = columns.filter(col => col.visible !== false);
  const hasActions = actions && actions.length > 0;

  return (
    <div className="card">
      {/* Header da tabela com busca e controles */}
      <div className="card-header border-0 pt-5">
        <TableSearch
          search={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={() => {
            if (onDataChange) {
              onDataChange({
                page: 1,
                pageSize: pagination.pageSize,
                search: searchTerm,
                sorting: sorting.field ? sorting : undefined,
              });
            }
          }}
          clearSearch={() => {
            if (onDataChange) {
              onDataChange({
                page: 1,
                pageSize: pagination.pageSize,
                search: '',
                sorting: sorting.field ? sorting : undefined,
              });
            }
          }
          }
          pageSize={pagination.pageSize}
          onPageSizeChange={(newPageSize) => {
            handlePaginationChange({
              ...pagination,
              pageSize: newPageSize,
              page: 1,
            });
          }}
          searchPlaceholder={searchPlaceholder}
          disabled={loading}
        />
      </div>

      {/* Corpo da tabela */}
      <div className="card-body py-3">
        <div className="table-responsive">
          <table className={tableClasses}>
            <TableHeader
              columns={visibleColumns}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              selectable={selectable}
              selection={selection}
              onSelectionChange={handleSelectionChange}
              totalRows={paginatedData.length}
            />

            {loading ? (
              <TableLoading
                columns={visibleColumns.length}
                selectable={selectable}
                hasActions={hasActions}
              />
            ) : error ? (
              <TableError
                message={error}
                onRetry={() => onDataChange && onDataChange({
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  search: debouncedSearchTerm,
                  sorting: sorting.field ? sorting : undefined,
                })}
                columns={visibleColumns.length}
                selectable={selectable}
                hasActions={hasActions}
              />
            ) : (
              <TableBody
                data={paginatedData}
                columns={visibleColumns}
                actions={actions}
                selectable={selectable}
                selection={selection}
                onSelectionChange={handleSelectionChange}
                striped={striped}
                hover={hover}
              />
            )}
          </table>
        </div>
      </div>

      {/* Footer da tabela com paginação */}
      {pagination.total > 0 && (
        <div className="card-footer py-4">
          <TableFooter
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            loading={loading}
            color="primary"
          />
        </div>
      )}
    </div>
  );
};

export default ModularDataTable;
